import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconDownload,
  IconEye,
  IconPaperclip,
} from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import {
  AttachmentType,
  isGeneratedOperativeReportAttachment,
  type ClaimAttachmentSummary,
} from '@billing/validation/attachment';
import {
  getAttachmentDownloadURL,
  useGetClaimAttachments,
} from 'api/resource/claimAttachment';
import { useDownloadFromSignedURL } from 'hooks';
import type { Claim } from 'interfaces';
import { ClaimAttachmentViewer } from './ClaimAttachmentViewer';
import { formatDateTime } from 'utils/dateUtils';

const attachmentTypeLabels: Record<
  AttachmentType,
  { key: string; fallback: string }
> = {
  [AttachmentType.PAYMENT_PROOF]: {
    key: 'paymentProof',
    fallback: 'Payment proof',
  },
  [AttachmentType.SUPPORTING_DOCUMENT]: {
    key: 'supportingDocument',
    fallback: 'Supporting document',
  },
  [AttachmentType.RECEIPT]: { key: 'receipt', fallback: 'Receipt' },
  [AttachmentType.INVOICE]: { key: 'invoice', fallback: 'Invoice' },
  [AttachmentType.CHAT_MESSAGE]: {
    key: 'chatMessage',
    fallback: 'Chat message',
  },
  [AttachmentType.OTHER]: { key: 'other', fallback: 'Other' },
};

const getAttachmentTypeLabel = (attachment: ClaimAttachmentSummary) =>
  isGeneratedOperativeReportAttachment(attachment)
    ? { key: 'operativeReport', fallback: 'Operative report' }
    : attachmentTypeLabels[attachment.type];

const AttachmentDownloadButton = ({
  attachmentID,
  originalFilename,
  label,
  onDownloadStart,
  onDownloadError,
}: {
  attachmentID: string;
  originalFilename: string;
  label: string;
  onDownloadStart: () => void;
  onDownloadError: () => void;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadAttachment = useDownloadFromSignedURL({
    getSignedURL: () => getAttachmentDownloadURL(attachmentID),
    filename: originalFilename,
    setLoading: setIsDownloading,
    onError: onDownloadError,
  });

  return (
    <Button
      size="xs"
      variant="light"
      leftSection={<IconDownload size={14} />}
      loading={isDownloading}
      onClick={() => {
        onDownloadStart();
        void downloadAttachment();
      }}
    >
      {label}
    </Button>
  );
};

export const ViewClaimAttachmentsModal = ({
  open,
  setOpen,
  claim,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  claim: Claim | null;
}) => {
  const { translate } = useTranslation();
  const [selectedAttachment, setSelectedAttachment] =
    useState<ClaimAttachmentSummary | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const attachments = useGetClaimAttachments({
    claimID: claim?.id,
    enabled: open,
  });

  const handleDownloadError = () =>
    setDownloadError(
      translate(
        'pages:claims.attachments.downloadError',
        'The attachment could not be downloaded.'
      )
    );

  const renderAttachments = () => {
    if (attachments.isLoading)
      return (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      );
    if (attachments.isError)
      return (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {translate(
            'pages:claims.attachments.loadError',
            'Claim attachments could not be loaded.'
          )}
        </Alert>
      );
    if (!attachments.data?.length)
      return (
        <Text c="dimmed" ta="center" py="xl">
          {translate(
            'pages:claims.attachments.empty',
            'No metadata-backed attachments have been added to this claim.'
          )}
        </Text>
      );
    return attachments.data.map((attachment) => (
      <Paper key={attachment.id} withBorder radius="md" p="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group
            gap="sm"
            align="flex-start"
            wrap="nowrap"
            style={{ minWidth: 0, flex: 1 }}
          >
            <IconPaperclip size={18} />
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={500} truncate>
                {attachment.originalFilename}
              </Text>
              <Group gap="xs">
                <Badge variant="light">
                  {translate(
                    `pages:claims.attachments.types.${
                      getAttachmentTypeLabel(attachment).key
                    }`,
                    getAttachmentTypeLabel(attachment).fallback
                  )}
                </Badge>
                <Text size="xs" c="dimmed">
                  {formatDateTime(attachment.createdAt, claim?.site?.timezone)}
                </Text>
              </Group>
              {attachment.createdByName ? (
                <Text size="xs" c="dimmed">
                  {translate(
                    'pages:claims.attachments.uploadedBy',
                    'Uploaded by'
                  )}{' '}
                  {attachment.createdByName}
                </Text>
              ) : null}
              {!attachment.createdByName && attachment.createdBy === null && (
                <Text size="xs" c="dimmed">
                  {translate(
                    'pages:claims.attachments.systemGenerated',
                    'System generated'
                  )}
                </Text>
              )}
            </Stack>
          </Group>
          <Group gap="xs">
            <Button
              type="button"
              size="xs"
              variant="light"
              leftSection={<IconEye size={14} />}
              onClick={() => setSelectedAttachment(attachment)}
            >
              {translate('pages:claims.attachments.view', 'View')}
            </Button>
            <AttachmentDownloadButton
              attachmentID={attachment.id}
              originalFilename={attachment.originalFilename}
              label={translate('pages:claims.attachments.download', 'Download')}
              onDownloadStart={() => setDownloadError(null)}
              onDownloadError={handleDownloadError}
            />
          </Group>
        </Group>
      </Paper>
    ));
  };

  return (
    <Modal
      opened={open}
      closeOnEscape={!selectedAttachment}
      trapFocus={!selectedAttachment}
      onClose={() => {
        setSelectedAttachment(null);
        setOpen(false);
      }}
      title={translate('pages:claims.attachments.title', 'Claim Attachments')}
      size="lg"
      centered
    >
      <Stack gap="sm">
        {renderAttachments()}
        {downloadError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {downloadError}
          </Alert>
        )}
      </Stack>
      {open &&
        selectedAttachment &&
        selectedAttachment.claimID === claim?.id && (
          <ClaimAttachmentViewer
            key={selectedAttachment.id}
            attachment={selectedAttachment}
            onClose={() => setSelectedAttachment(null)}
          />
        )}
    </Modal>
  );
};
